import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Card, 
  Form, 
  Input, 
  Button, 
  message, 
  Spin, 
  Row, 
  Col, 
  InputNumber, 
  Divider, 
  Space, 
  Alert,
  Table,
  Tag,
  Modal,
  Tooltip
} from 'antd';
import { useMutation } from 'react-query';
import { 
  BuildOutlined, 
  PlayCircleOutlined, 
  InfoCircleOutlined,
  CopyOutlined,
  DownloadOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { taskManagerClient, TaskCreateRequest } from '@/utils/taskManager';
import { getVisitorId } from '@/utils/session';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

// 基因合成参数接口
interface GeneSynthesisParams {
  sequences: string;
  target_oligo_len: number;
  min_oligo_len: number;
  max_oligo_len: number;
  target_overlap_tm: number;
  min_overlap: number;
  max_overlap: number;
  na_molar: number;
  w_tm: number;
  w_len: number;
  w_misprime: number;
}

// 寡核结果接口
interface OligoResult {
  index: number;
  orientation: string;
  start: number;
  end: number;
  length: number;
  sequence: string;
  overlap_tm_to_prev?: number;
  overlap_tm_to_next?: number;
}

// 序列设计结果接口
interface SequenceDesignResult {
  sequence_id: string;
  sequence_length?: number;
  oligo_count?: number;
  oligos?: OligoResult[];
  success: boolean;
  error?: string;
  // 支持嵌套结构（任务管理器返回的格式）
  result?: {
  sequence_id: string;
  sequence_length: number;
  oligo_count: number;
  oligos: OligoResult[];
  success: boolean;
  error?: string;
  };
}

// 任务结果接口
interface TaskResult {
  total_sequences: number;
  successful_count: number;
  failed_count: number;
  results: SequenceDesignResult[];
  parameters: GeneSynthesisParams;
}

export default function GeneSynthesisPage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [results, setResults] = useState<TaskResult | null>(null);
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const [selectedSequence, setSelectedSequence] = useState<SequenceDesignResult | null>(null);
  const [oligoModalVisible, setOligoModalVisible] = useState(false);

  // 默认参数
  const defaultParams = {
    target_oligo_len: 60,
    min_oligo_len: 30,
    max_oligo_len: 60,
    target_overlap_tm: 60.0,
    min_overlap: 15,
    max_overlap: 20,
    na_molar: 0.05,
    w_tm: 1.0,
    w_len: 0.01,
    w_misprime: 1.0,
  };

  // 设置默认值
  useEffect(() => {
    form.setFieldsValue(defaultParams);
  }, [form]);

  // 轮询任务结果
  const pollTaskResult = async (taskId: string): Promise<any> => {
    const apiBase = import.meta.env.VITE_TASK_MANAGER_URL?.replace(/\/$/, '') || '/task_manager';
    const maxAttempts = 60; // 最多轮询60次
    const pollInterval = 2000; // 每2秒轮询一次

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const response = await fetch(`${apiBase}/tasks/${taskId}/status`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const taskStatus = await response.json();
        console.log(`轮询第${attempt + 1}次，任务状态:`, taskStatus.status);

        if (taskStatus.status === 'completed') {
          return taskStatus.result;
        } else if (taskStatus.status === 'failed') {
          throw new Error(taskStatus.error || '任务执行失败');
        }

        // 等待后继续轮询
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      } catch (error) {
        console.error(`轮询第${attempt + 1}次失败:`, error);
        if (attempt === maxAttempts - 1) {
          throw error;
        }
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }
    }

    throw new Error('任务超时，请检查任务状态');
  };

  // 提交任务
  const submitTaskMutation = useMutation(
    async (params: GeneSynthesisParams) => {
      const visitorId = getVisitorId();
      
      const taskRequest: TaskCreateRequest = {
        name: '基因合成引物设计',
        service_name: 'clone_service',
        task_type: 'gene_synthesis',
        handler_name: 'gene_synthesis_predict_json',  // 更新为新的handler名称
        parameters: params
        // 移除 user_id，让后端从认证cookie中自动获取
      };

      console.log('创建任务请求:', taskRequest);
      const task = await taskManagerClient.createTask(taskRequest);
      console.log('任务创建成功:', task);
      
      setCurrentTaskId(task.id);
      
      // 开始轮询结果
      const result = await pollTaskResult(task.id);
      console.log('任务完成，结果:', result);
      
      return result;
    },
    {
      onSuccess: (data) => {
        // 任务管理器返回的数据结构是 { result: { actual_data } }
        const actualResult = data?.result || data;
        console.log('基因合成任务完成，原始数据:', data);
        console.log('解析后的结果:', actualResult);
        setResults(actualResult);
        message.success('基因合成引物设计完成！');
        setCurrentTaskId(null);
      },
      onError: (error: any) => {
        console.error('基因合成引物设计失败:', error);
        message.error(`基因合成引物设计失败: ${error.message || error}`);
        setCurrentTaskId(null);
      }
    }
  );

  // 处理表单提交
  const handleSubmit = async (values: any) => {
    try {
      if (!values.sequences?.trim()) {
        message.error('请输入序列');
        return;
      }

      const params: GeneSynthesisParams = {
        sequences: values.sequences.trim(),
        target_oligo_len: values.target_oligo_len ?? defaultParams.target_oligo_len,
        target_overlap_tm: values.target_overlap_tm ?? defaultParams.target_overlap_tm,
        min_oligo_len: values.min_oligo_len ?? defaultParams.min_oligo_len,
        max_oligo_len: values.max_oligo_len ?? defaultParams.max_oligo_len,
        min_overlap: values.min_overlap ?? defaultParams.min_overlap,
        max_overlap: values.max_overlap ?? defaultParams.max_overlap,
        na_molar: values.na_molar ?? defaultParams.na_molar,
        w_tm: values.w_tm ?? defaultParams.w_tm,
        w_len: values.w_len ?? defaultParams.w_len,
        w_misprime: values.w_misprime ?? defaultParams.w_misprime,
      };

      await submitTaskMutation.mutateAsync(params);
    } catch (error) {
      console.error('提交失败:', error);
    }
  };

  // 重置表单
  const handleReset = () => {
    form.resetFields();
    form.setFieldsValue(defaultParams);
    setResults(null);
    setCurrentTaskId(null);
  };

  // 复制序列到剪贴板
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success('已复制到剪贴板');
    });
  };

  // 下载结果
  const downloadResults = (sequenceResult: SequenceDesignResult) => {
    // 兼容两种数据结构：sequenceResult.oligos 或 sequenceResult.result.oligos
    const oligos = sequenceResult.result?.oligos || sequenceResult.oligos;
    if (!oligos || !Array.isArray(oligos)) {
        message.error('没有可下载的引物数据');
      return;
    }
    
    const content = oligos.map((oligo, index) => 
      `>Oligo_${oligo.index}_${oligo.orientation}\n${oligo.sequence}`
    ).join('\n');
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${sequenceResult.sequence_id}_oligos.fasta`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // 寡核表格列定义
  const oligoColumns = [
    {
      title: '索引',
      dataIndex: 'index',
      key: 'index',
      width: 60,
    },
    {
      title: '方向',
      dataIndex: 'orientation',
      key: 'orientation',
      width: 60,
      render: (orientation: string) => (
        <Tag color={orientation === 'F' ? 'blue' : 'green'}>{orientation}</Tag>
      ),
    },
    {
      title: '位置',
      key: 'position',
      width: 100,
      render: (record: OligoResult) => `${record.start}-${record.end}`,
    },
    {
      title: '长度',
      dataIndex: 'length',
      key: 'length',
      width: 60,
    },
    {
      title: '序列',
      dataIndex: 'sequence',
      key: 'sequence',
      width: 300,
      render: (sequence: string) => (
        <Text code style={{ fontSize: '12px', wordBreak: 'break-all' }}>
          {sequence}
        </Text>
      ),
    },
    {
      title: '重叠Tm (°C)',
      key: 'overlap_tm',
      width: 160,
      render: (record: OligoResult) => {
        const parts: string[] = [];
        if (typeof record.overlap_tm_to_prev === 'number') {
          parts.push(`前: ${record.overlap_tm_to_prev.toFixed(1)}°C`);
        }
        if (typeof record.overlap_tm_to_next === 'number') {
          parts.push(`后: ${record.overlap_tm_to_next.toFixed(1)}°C`);
        }
        if (parts.length === 0) return <Text type="secondary">-</Text>;
        return (
          <Text style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
            {parts.join(' / ')}
          </Text>
        );
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      render: (record: OligoResult) => (
        <Button
          type="link"
          size="small"
          icon={<CopyOutlined />}
          onClick={() => copyToClipboard(record.sequence)}
        >
          复制
        </Button>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px' }}>
      {/* 返回按钮 */}
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/cloning')}
        style={{ marginBottom: 16 }}
      >
        返回分子克隆
      </Button>
      
      {/* 页面标题 */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <Title level={2}>
          <BuildOutlined style={{ marginRight: 8, color: '#722ed1' }} />
          基因合成引物设计
        </Title>
        <Paragraph style={{ fontSize: '16px', color: '#666' }}>
          为基因合成设计PCR引物，支持多序列批量处理，自动优化寡核长度和重叠区域
        </Paragraph>
      </div>

      {/* 上半部分：序列输入和参数设置 */}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={defaultParams}
      >
        <Row gutter={24} style={{ marginBottom: 24 }}>
          {/* 左侧：序列输入区 */}
          <Col xs={24} lg={18}>
            <Card title="序列输入" style={{ height: '100%' }}>
              <Form.Item
                name="sequences"
                label="输入序列"
                rules={[{ required: true, message: '请输入序列' }]}
              >
                <TextArea
                  rows={15}
                  placeholder="请输入FASTA格式序列（支持多序列）&#10;例如：&#10;>Gene1&#10;ATGCGTAAC...&#10;>Gene2&#10;ATGAAACGT..."
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />
              </Form.Item>

              {/* 操作按钮 */}
              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<PlayCircleOutlined />}
                    loading={submitTaskMutation.isLoading}
                    disabled={!!currentTaskId}
                    size="large"
                  >
                    开始设计
                  </Button>
                  <Button onClick={handleReset} size="large">
                    重置
                  </Button>
                </Space>
              </Form.Item>
            </Card>
          </Col>

        {/* 右侧：参数设置区 */}
        <Col xs={24} lg={6}>
          <Card title="参数设置" style={{ height: '100%' }} size="small">
            <div style={{ padding: '8px 0' }}>
              {/* 退火温度 */}
              <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '13px' }}>退火温度 (°C):</span>
                <Form.Item name="target_overlap_tm" style={{ margin: 0 }}>
                  <InputNumber 
                    min={40} 
                    max={85} 
                    step={0.1} 
                    size="small" 
                    style={{ width: '80px' }}
                    placeholder="60.0"
                  />
                </Form.Item>
              </div>

              {/* 引物最小长度 */}
              <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '13px' }}>引物最小长度 (bp):</span>
                <Form.Item name="min_oligo_len" style={{ margin: 0 }}>
                  <InputNumber 
                    min={10} 
                    max={150} 
                    size="small" 
                    style={{ width: '80px' }}
                    placeholder="30"
                  />
                </Form.Item>
              </div>

              {/* 引物最大长度 */}
              <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '13px' }}>引物最大长度 (bp):</span>
                <Form.Item name="max_oligo_len" style={{ margin: 0 }}>
                  <InputNumber 
                    min={20} 
                    max={300} 
                    size="small" 
                    style={{ width: '80px' }}
                    placeholder="60"
                  />
                </Form.Item>
              </div>

              {/* 最小重叠长度 */}
              <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '13px' }}>最小重叠长度 (bp):</span>
                <Form.Item name="min_overlap" style={{ margin: 0 }}>
                  <InputNumber 
                    min={8} 
                    max={50} 
                    size="small" 
                    style={{ width: '80px' }}
                    placeholder="15"
                  />
                </Form.Item>
              </div>

              {/* 最大重叠长度 */}
              <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '13px' }}>最大重叠长度 (bp):</span>
                <Form.Item name="max_overlap" style={{ margin: 0 }}>
                  <InputNumber 
                    min={15} 
                    max={80} 
                    size="small" 
                    style={{ width: '80px' }}
                    placeholder="20"
                  />
                </Form.Item>
              </div>
            </div>
          </Card>
        </Col>
        </Row>
      </Form>

      {/* 下半部分：结果显示区 */}
      <Row>
        <Col span={24}>
          <Card title="设计结果">
            {submitTaskMutation.isLoading && (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>
                  <Text>正在设计引物，请稍候...</Text>
                </div>
              </div>
            )}

            {results && (
              <div>
                {/* 结果统计 */}
                <Alert
                  message="设计完成"
                  description={
                    <div>
                      <Text>总序列数: {results.total_sequences}</Text>
                      <Divider type="vertical" />
                      <Text style={{ color: '#52c41a' }}>成功: {results.successful_count}</Text>
                      <Divider type="vertical" />
                      <Text style={{ color: '#f5222d' }}>失败: {results.failed_count}</Text>
                    </div>
                  }
                  type="success"
                  style={{ marginBottom: 16 }}
                />

                {/* 序列结果列表 */}
                {results.results.map((sequenceResult, index) => (
                  <Card
                    key={index}
                    size="small"
                    title={
                      <Space>
                        <Text strong>{sequenceResult.sequence_id}</Text>
                        <Tag color={sequenceResult.success ? 'green' : 'red'}>
                          {sequenceResult.success ? '成功' : '失败'}
                        </Tag>
                        {sequenceResult.success && (
                            <Text type="secondary">
                              长度: {sequenceResult.result?.sequence_length || sequenceResult.sequence_length} bp, 
                              引物数量: {sequenceResult.result?.oligo_count || sequenceResult.oligo_count}
                            </Text>
                        )}
                      </Space>
                    }
                    extra={
                      sequenceResult.success && (
                        <Space>
                          <Button
                            size="small"
                            onClick={() => {
                              setSelectedSequence(sequenceResult);
                              setOligoModalVisible(true);
                            }}
                          >
                            查看详情
                          </Button>
                          <Button
                            size="small"
                            icon={<DownloadOutlined />}
                            onClick={() => downloadResults(sequenceResult)}
                          >
                            下载
                          </Button>
                        </Space>
                      )
                    }
                    style={{ marginBottom: 16 }}
                  >
                    {sequenceResult.success ? (
                      <div>
                        <Text>成功设计 {sequenceResult.result?.oligo_count || sequenceResult.oligo_count} 个寡核引物</Text>
                      </div>
                    ) : (
                      <Alert
                        message="设计失败"
                        description={sequenceResult.error}
                        type="error"
                        showIcon
                      />
                    )}
                  </Card>
                ))}
              </div>
            )}

            {!results && !submitTaskMutation.isLoading && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <InfoCircleOutlined style={{ fontSize: '48px', marginBottom: 16 }} />
                <div>请输入序列并设置参数，然后点击"开始设计"</div>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* 寡核详情弹窗 */}
      <Modal
        title={`寡核详情 - ${selectedSequence?.sequence_id}`}
        open={oligoModalVisible}
        onCancel={() => setOligoModalVisible(false)}
        footer={null}
        width={1200}
      >
        {selectedSequence && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>序列长度: </Text>
              <Text>{selectedSequence.result?.sequence_length || selectedSequence.sequence_length} bp</Text>
              <Divider type="vertical" />
                <Text strong>引物数量: </Text>
                <Text>{selectedSequence.result?.oligo_count || selectedSequence.oligo_count}</Text>
            </div>

            {/* 引物设计Mapping图谱 */}
            {(() => {
                  const oligos = selectedSequence.result?.oligos || selectedSequence.oligos || [];
                  if (oligos.length === 0) return null;
                  
                  const totalLength = selectedSequence.result?.sequence_length || selectedSequence.sequence_length || 0;
                  const baseWidth = 10; // 单个碱基字符的宽度（px）
                  const baseGap = 2; // 碱基之间的间隔（px）
                  const baseUnitWidth = baseWidth + baseGap; // 单个碱基单元占用总宽度
                  const prefixOffset = 32; // 左侧前缀区域宽度（用于显示5'标签）
                  const suffixOffset = 32; // 右侧后缀区域宽度（用于显示3'标签）
                  const sequencePixelWidth = prefixOffset + suffixOffset + totalLength * baseUnitWidth;
                  const getBaseLeft = (position: number) => prefixOffset + position * baseUnitWidth;
                  const getPrimerLeft = (start: number) => getBaseLeft(start);
                  const getPrimerCenterLeft = (start: number, length: number) => getBaseLeft(start) + (length * baseUnitWidth) / 2;
                  const getPrimerBlockLeft = (start: number) => getPrimerLeft(start) - 1; // 调整边框带来的偏移
                  const strandHeight = baseWidth + 6;
                  const strandLabelFontSize = 10;
                  const strandLabelTop = (strandHeight - strandLabelFontSize) / 2;
                  
                  // 从引物重建完整目标序列
                  const reconstructTargetSequence = () => {
                    const sortedOligos = [...oligos].sort((a, b) => a.start - b.start);
                    let targetSeq = new Array(totalLength).fill(''); // 初始化为空
                    
                    // 使用所有引物填充目标序列
                    sortedOligos.forEach((oligo: any) => {
                      if (oligo.orientation === 'F') {
                        // 正向引物直接填充
                        for (let i = 0; i < oligo.sequence.length && (oligo.start + i) < totalLength; i++) {
                          if (!targetSeq[oligo.start + i]) { // 只在空位填充
                            targetSeq[oligo.start + i] = oligo.sequence[i];
                          }
                        }
                      } else if (oligo.orientation === 'R') {
                        // 反向引物需要反向互补后填充
                        const complementSeq = getReverseComplement(oligo.sequence);
                        for (let i = 0; i < complementSeq.length && (oligo.start + i) < totalLength; i++) {
                          if (!targetSeq[oligo.start + i]) { // 只在空位填充
                            targetSeq[oligo.start + i] = complementSeq[i];
                          }
                        }
                      }
                    });
                    
                    // 填充剩余的空位（使用相邻引物推断）
                    for (let i = 0; i < targetSeq.length; i++) {
                      if (!targetSeq[i]) {
                        // 寻找最近的已知碱基
                        let leftBase = '';
                        let rightBase = '';
                        for (let j = i - 1; j >= 0; j--) {
                          if (targetSeq[j]) {
                            leftBase = targetSeq[j];
                            break;
                          }
                        }
                        for (let j = i + 1; j < targetSeq.length; j++) {
                          if (targetSeq[j]) {
                            rightBase = targetSeq[j];
                            break;
                          }
                        }
                        // 简单填充策略：使用A作为默认
                        targetSeq[i] = leftBase || rightBase || 'A';
                      }
                    }
                    
                    return targetSeq.join('');
                  };
                  
                  // 获取反向互补序列
                  const getComplementBase = (base: string) => {
                    const complement: { [key: string]: string } = {
                      'A': 'T', 'T': 'A', 'G': 'C', 'C': 'G', 'N': 'N'
                    };
                    return complement[base.toUpperCase()] || 'N';
                  };
                  
                  const getReverseComplement = (sequence: string) => {
                    return sequence.split('').reverse().map(base => getComplementBase(base)).join('');
                  };
                  
                  const targetSequence = reconstructTargetSequence();
                  
                    return (
                      <div>
                        {/* 横向滚动的序列映射区域 - 不换行连续显示 */}
                  <div style={{ 
                        overflowX: 'auto', 
                        overflowY: 'hidden',
                        border: '1px solid #d9d9d9',
                        borderRadius: '4px',
                        backgroundColor: 'white',
                        padding: '16px'
                      }}>
                        <div style={{ 
                          fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                          fontSize: '11px',
                          lineHeight: '1.4',
                          whiteSpace: 'nowrap',
                          minWidth: `${sequencePixelWidth}px`
                        }}>
                          {/* 位置标尺 */}
                          <div style={{ 
                            color: '#666',
                            marginBottom: '4px',
                            fontSize: '9px',
                            height: '12px',
                            position: 'relative'
                          }}>
                            {Array.from({ length: Math.ceil(targetSequence.length / 10) }, (_, i) => {
                              const pos = i * 10 + 1;
                              return (
                                <span key={i} style={{ 
                                  position: 'absolute', 
                                  left: `${getBaseLeft(pos - 1)}px`,
                                  transform: 'translateX(-50%)'
                                }}>
                                  {pos <= targetSequence.length ? pos : ''}
                                </span>
                              );
                            })}
                          </div>
                          
                          {/* R引物标签层 */}
                          <div style={{ 
                            height: '12px', 
                            marginBottom: '2px',
                            position: 'relative',
                            fontSize: '8px'
                          }}>
                            {oligos
                              .filter((oligo: any) => oligo.orientation === 'R')
                              .map((oligo: any, idx: number) => {
                                const baseCount = (oligo.sequence || '').length || oligo.length || 0;
                                return (
                                  <span
                                    key={idx}
                                    style={{
                                      position: 'absolute',
                                      left: `${getPrimerCenterLeft(oligo.start, baseCount)}px`,
                                      color: '#fa8c16',
                                      fontWeight: 'bold',
                                      transform: 'translateX(-50%)',
                                    }}
                                  >
                                    {oligo.orientation}{oligo.index}
                                  </span>
                                );
                              })}
                          </div>

                          {/* 上方引物层（反向引物） */}
                          <div style={{ 
                            height: `${baseWidth + 4}px`,
                            marginBottom: '2px',
                            position: 'relative'
                          }}>
                            {oligos
                              .filter((oligo: any) => oligo.orientation === 'R')
                              .map((oligo: any, idx: number) => {
                                const baseCount = (oligo.sequence || '').length || oligo.length || 0;
                                // 反向引物显示其原序列的反序（3' -> 5' 方向）
                                const reverseSeq = oligo.sequence.split('').reverse().join('');
                                
                                return (
                                  <div
                                    key={idx}
                                    style={{
                                      position: 'absolute',
                                      left: `${getPrimerLeft(oligo.start)}px`,
                                      backgroundColor: 'transparent',
                                      color: '#fa8c16',
                                      padding: '0',
                                      borderRadius: 0,
                                      border: 'none',
                                      fontSize: '10px',
                                      fontFamily: 'monospace',
                                      height: '100%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      boxSizing: 'border-box'
                                    }}
                                    title={`R${oligo.index}: 原序列 ${oligo.sequence} | 反序显示 ${reverseSeq} (3'→5')`}
                                  >
                                    {Array.from({ length: baseCount }).map((_, baseIdx: number) => (
                                      <span key={baseIdx} style={{ 
                                        width: `${baseUnitWidth}px`,
                                        textAlign: 'center',
                                        display: 'inline-block',
                                        flexShrink: 0
                                      }}>
                                        {reverseSeq[baseIdx] || ''}
                                      </span>
                                    ))}
                                  </div>
                                );
                              })}
                          </div>

                          {/* R引物与模板对齐线 */}
                          <div style={{ 
                            height: '8px',
                            marginBottom: '2px',
                            position: 'relative'
                          }}>
                            {oligos
                              .filter((oligo: any) => oligo.orientation === 'R')
                              .map((oligo: any, idx: number) => (
                                <div
                                  key={idx}
                                  style={{
                                    position: 'absolute',
                                      left: `${getPrimerLeft(oligo.start)}px`,
                                    display: 'flex'
                                  }}
                                >
                                  {Array.from({ length: (oligo.sequence || '').length || oligo.length || 0 }).map((_, baseIdx) => (
                                    <span
                                      key={baseIdx}
                                      style={{
                                        width: `${baseUnitWidth}px`,
                                        textAlign: 'center',
                                        fontSize: '8px',
                                        color: '#fa8c16',
                                        display: 'inline-block',
                                        flexShrink: 0
                                      }}
                                    >
                                      |
                                    </span>
                                  ))}
                                </div>
                              ))}
                          </div>
                          
                          {/* 目标序列双链（中间） */}
                          <div style={{ 
                            backgroundColor: '#e6f7ff', 
                            padding: '4px 0',
                            borderRadius: '3px',
                            border: '2px solid #1890ff',
                            marginBottom: '2px',
                            position: 'relative'
                          }}>
                            {/* 正链 (5' -> 3') */}
                            <div style={{ 
                              height: `${baseWidth + 6}px`,
                              marginBottom: '2px',
                              position: 'relative'
                            }}>
                              <span style={{
                                position: 'absolute',
                                left: `${prefixOffset - baseUnitWidth}px`,
                                top: `${strandLabelTop}px`,
                                color: '#1890ff',
                                fontWeight: 'bold',
                                fontSize: '10px'
                              }}>
                                5'
                              </span>
                              <div style={{
                                marginLeft: `${prefixOffset}px`,
                                display: 'flex',
                                alignItems: 'center'
                              }}>
                                {targetSequence.split('').map((base, index) => (
                                  <span key={index} style={{ 
                                    width: `${baseUnitWidth}px`,
                                    textAlign: 'center',
                                    color: '#1890ff',
                                    fontFamily: 'monospace',
                                    fontWeight: 'normal'
                                  }}>
                                    {base}
                                  </span>
                                ))}
                              </div>
                              <span style={{
                                position: 'absolute',
                                left: `${prefixOffset + totalLength * baseUnitWidth}px`,
                                top: `${strandLabelTop}px`,
                                color: '#1890ff',
                                fontWeight: 'bold',
                                fontSize: '10px'
                              }}>
                                3'
                              </span>
                            </div>

                            {/* 反链 (3' -> 5') */}
                            <div style={{ 
                              height: `${baseWidth + 6}px`,
                              marginTop: '2px',
                              position: 'relative'
                            }}>
                              <span style={{
                                position: 'absolute',
                                left: `${prefixOffset - baseUnitWidth}px`,
                                top: `${strandLabelTop}px`,
                                color: '#1890ff',
                                fontWeight: 'bold',
                                fontSize: '10px'
                              }}>
                                3'
                              </span>
                              <div style={{
                                marginLeft: `${prefixOffset}px`,
                                display: 'flex',
                                alignItems: 'center'
                              }}>
                                {targetSequence.split('').map((base, index) => {
                                  const complement = getComplementBase(base);
                                  return (
                                    <span key={index} style={{ 
                                      width: `${baseUnitWidth}px`,
                                      textAlign: 'center',
                                      color: '#1890ff',
                                      fontFamily: 'monospace',
                                      fontWeight: 'normal'
                                    }}>
                                      {complement}
                                    </span>
                                  );
                                })}
                              </div>
                              <span style={{
                                position: 'absolute',
                                left: `${prefixOffset + totalLength * baseUnitWidth}px`,
                                top: `${strandLabelTop}px`,
                                color: '#1890ff',
                                fontWeight: 'bold',
                                fontSize: '10px'
                              }}>
                                5'
                              </span>
                            </div>
                          </div>

                          {/* F引物与模板对齐线 */}
                          <div style={{ 
                            height: '8px',
                            marginTop: '2px',
                            position: 'relative'
                          }}>
                            {oligos
                              .filter((oligo: any) => oligo.orientation === 'F')
                              .map((oligo: any, idx: number) => (
                                <div
                                  key={idx}
                                  style={{
                                    position: 'absolute',
                                      left: `${getPrimerLeft(oligo.start)}px`,
                                    display: 'flex'
                                  }}
                                >
                                  {Array.from({ length: (oligo.sequence || '').length || oligo.length || 0 }).map((_, baseIdx) => (
                                    <span
                                      key={baseIdx}
                                      style={{
                                        width: `${baseUnitWidth}px`,
                                        textAlign: 'center',
                                        fontSize: '8px',
                                        color: '#52c41a',
                                        display: 'inline-block',
                                        flexShrink: 0
                                      }}
                                    >
                                      |
                                    </span>
                                  ))}
                                </div>
                              ))}
                          </div>

                          {/* 下方引物层（正向引物） */}
                          <div style={{ 
                            height: `${baseWidth + 6}px`, 
                            marginTop: '2px',
                            position: 'relative'
                          }}>
                            {oligos
                              .filter((oligo: any) => oligo.orientation === 'F')
                              .map((oligo: any, idx: number) => {
                                const baseCount = (oligo.sequence || '').length || oligo.length || 0;
                                return (
                                  <div
                                    key={idx}
                                    style={{
                                      position: 'absolute',
                                      left: `${getPrimerLeft(oligo.start)}px`,
                                      backgroundColor: 'transparent',
                                    color: '#52c41a',
                                    padding: '0',
                                      borderRadius: 0,
                                      border: 'none',
                                    fontSize: '10px',
                                    fontFamily: 'monospace',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    boxSizing: 'border-box'
                                  }}
                                  title={`F${oligo.index}: ${oligo.sequence}`}
                                  >
                                    {Array.from({ length: baseCount }).map((_, baseIdx: number) => (
                                      <span key={baseIdx} style={{ 
                                        width: `${baseUnitWidth}px`,
                                        textAlign: 'center',
                                        display: 'inline-block',
                                        flexShrink: 0
                                      }}>
                                        {oligo.sequence[baseIdx] || ''}
                                      </span>
                                    ))}
                                  </div>
                                );
                              })}
                          </div>

                          {/* F引物标签层 */}
                          <div style={{ 
                            height: '12px', 
                            marginTop: '4px',
                            position: 'relative',
                            fontSize: '8px'
                          }}>
                            {oligos
                              .filter((oligo: any) => oligo.orientation === 'F')
                              .map((oligo: any, idx: number) => {
                                const baseCount = (oligo.sequence || '').length || oligo.length || 0;
                                return (
                                  <span
                                    key={idx}
                                    style={{
                                      position: 'absolute',
                                      left: `${getPrimerCenterLeft(oligo.start, baseCount)}px`,
                                      color: '#52c41a',
                                      fontWeight: 'bold',
                                      transform: 'translateX(-50%)',
                                    }}
                                  >
                                    {oligo.orientation}{oligo.index}
                                  </span>
                                );
                              })}
                          </div>
                        </div>
                      </div>

                  {/* 图例 */}
                      <div style={{ marginTop: '16px' }}>
                    <Space>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ 
                              width: '20px', 
                          height: '16px', 
                              backgroundColor: '#e6f7ff',
                              border: '2px solid #1890ff',
                              borderRadius: '2px',
                              marginRight: '8px'
                            }} />
                            <Text style={{ fontSize: '12px' }}>目标序列</Text>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ 
                              width: '20px', 
                          height: '16px', 
                              backgroundColor: '#f6ffed',
                              border: '1px solid #52c41a',
                              borderRadius: '2px',
                              marginRight: '8px'
                            }} />
                            <Text style={{ fontSize: '12px' }}>正向引物 (F)</Text>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ 
                              width: '20px', 
                              height: '16px', 
                          backgroundColor: '#fff2e8', 
                              border: '1px solid #fa8c16',
                              borderRadius: '2px',
                              marginRight: '8px'
                            }} />
                            <Text style={{ fontSize: '12px' }}>反向引物 (R)</Text>
                      </div>
                    </Space>
                  </div>
                </div>
                  );
                })()}
            
            <Table
              columns={oligoColumns}
              dataSource={selectedSequence.result?.oligos || selectedSequence.oligos}
              rowKey="index"
              size="small"
              pagination={false}
              scroll={{ x: 1000 }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
